import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getMediaInfo, getMediaUrl, updateMedia } from "../api/media";
import LoadingSpinner from "../components/Common/LoadingSpinner";

export default function MediaTextEditor() {
  const { mediaId } = useParams<{ mediaId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Editor state
  const [editedText, setEditedText] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // PDF blob URL for authenticated viewing
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfBlobUrlRef = useRef<string | null>(null);

  // Fetch media info - always get fresh data
  const { data: media, isLoading, error } = useQuery({
    queryKey: ["media", mediaId, "editor"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getMediaInfo(Number(mediaId), token);
    },
    enabled: !!mediaId,
    staleTime: 0, // Always refetch
    refetchOnMount: "always",
  });

  // Initialize edited text when media loads
  useEffect(() => {
    if (media?.ocr_text) {
      setEditedText(media.ocr_text);
    }
  }, [media?.ocr_text]);

  // Track changes
  useEffect(() => {
    if (media) {
      setHasChanges(editedText !== (media.ocr_text || ""));
    }
  }, [editedText, media]);

  // Fetch PDF with authentication
  useEffect(() => {
    if (!media || media.content_type !== "application/pdf") {
      return;
    }

    const fetchPdf = async () => {
      setPdfLoading(true);
      setPdfError(null);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(getMediaUrl(media.id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to load PDF");
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        pdfBlobUrlRef.current = url;
        setPdfBlobUrl(url);
      } catch (err) {
        setPdfError(err instanceof Error ? err.message : "Failed to load PDF");
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdf();

    return () => {
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, [media, getAccessTokenSilently]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (text: string) => {
      const token = await getAccessTokenSilently();
      return updateMedia(Number(mediaId), { ocr_text: text }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", mediaId] });
      queryClient.invalidateQueries({ queryKey: ["globalMediaLibrary"] });
      queryClient.invalidateQueries({ queryKey: ["mediaLibrary"] });
      setSaveSuccess(true);
      setHasChanges(false);
      setTimeout(() => setSaveSuccess(false), 2000);
    },
  });

  const handleSave = useCallback(() => {
    saveMutation.mutate(editedText);
  }, [editedText, saveMutation]);

  const handleReset = useCallback(() => {
    if (media?.ocr_text) {
      setEditedText(media.ocr_text);
    } else {
      setEditedText("");
    }
  }, [media?.ocr_text]);

  // Word count
  const wordCount = editedText.trim() ? editedText.trim().split(/\s+/).length : 0;
  const charCount = editedText.length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-vault-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !media) {
    return (
      <div className="min-h-screen bg-vault-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load media</p>
          <Link to="/media" className="text-vault-600 hover:underline">
            Back to Media Library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vault-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-vault-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-vault-600 hover:text-vault-800"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Back
            </button>
            <div className="h-6 w-px bg-vault-200" />
            <h1 className="text-lg font-semibold text-vault-900 truncate max-w-md">
              {media.caption || media.original_filename}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {saveSuccess && (
              <span className="text-green-600 text-sm">✓ Saved</span>
            )}
            {saveMutation.isError && (
              <span className="text-red-600 text-sm">Save failed</span>
            )}
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className={`px-4 py-2 rounded text-sm font-medium ${
                hasChanges
                  ? "bg-vault-100 text-vault-700 hover:bg-vault-200"
                  : "bg-vault-50 text-vault-400 cursor-not-allowed"
              }`}
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className={`px-4 py-2 rounded text-sm font-medium ${
                hasChanges && !saveMutation.isPending
                  ? "bg-vault-600 text-white hover:bg-vault-700"
                  : "bg-vault-300 text-vault-500 cursor-not-allowed"
              }`}
            >
              {saveMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </header>

      {/* Main content - side by side */}
      <main className="flex-1 flex overflow-hidden">
        {/* PDF Viewer */}
        <div className="w-1/2 border-r border-vault-200 bg-vault-100 flex flex-col">
          <div className="p-3 bg-white border-b border-vault-200 text-sm font-medium text-vault-700">
            Document Preview
          </div>
          <div className="flex-1 p-4 overflow-hidden">
            {media.content_type === "application/pdf" ? (
              <div className="w-full h-full bg-white rounded shadow-sm flex items-center justify-center">
                {pdfLoading && (
                  <div className="flex flex-col items-center gap-2 text-vault-500">
                    <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Loading PDF...</span>
                  </div>
                )}
                {pdfError && (
                  <div className="text-red-500 text-center">
                    <p>Failed to load PDF</p>
                    <p className="text-sm">{pdfError}</p>
                  </div>
                )}
                {pdfBlobUrl && !pdfLoading && (
                  <iframe
                    src={pdfBlobUrl}
                    className="w-full h-full rounded"
                    title={media.original_filename}
                  />
                )}
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-vault-400">
                <div className="text-center">
                  <span className="text-6xl">📄</span>
                  <p className="mt-2">{media.original_filename}</p>
                  <p className="text-sm text-vault-500 mt-1">
                    Preview not available for this file type
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Text Editor */}
        <div className="w-1/2 flex flex-col bg-white">
          <div className="p-3 border-b border-vault-200 flex items-center justify-between">
            <span className="text-sm font-medium text-vault-700">Extracted Text</span>
            <span className="text-xs text-vault-500">
              {wordCount.toLocaleString()} words · {charCount.toLocaleString()} characters
            </span>
          </div>
          <div className="flex-1 p-4 overflow-hidden">
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              placeholder="No text has been extracted yet. Run text extraction first, then edit here."
              className="w-full h-full resize-none rounded border border-vault-200 p-4 text-sm font-mono text-vault-800 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
              spellCheck={true}
            />
          </div>
          {!media.ocr_text && (
            <div className="p-4 bg-amber-50 border-t border-amber-100">
              <p className="text-sm text-amber-700">
                No text has been extracted from this document yet. 
                <Link to="/media" className="ml-1 underline hover:text-amber-800">
                  Go back to the Media Library
                </Link> and click &quot;Extract Text&quot; first.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
